import { SerializedError } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'

import createAppSlice from 'state/createAppSlice'

import {
  Activity,
  FlatInteractionModelData,
  InteractionModel,
  NestableGroupValue
} from 'types/shared'

import api from 'utils/api'

import { getProperty } from 'utils/helpers/object'

import { setFlatInteractionModelActivities } from 'utils/interactionModel'

import { interactionModelDataToFlat } from '../utils/interactionModelDataToFlat/interactionModelDataToFlat'

import * as googleTagManager from 'modules/shared/GTM'

const requestPending = (state: InteractionModel): InteractionModel => ({
  ...state,
  isFetching: true,
  error: ''
})

const requestError = (
  state: InteractionModel,
  action: { error: SerializedError }
): InteractionModel => ({
  ...state,
  flatData: undefined,
  error: action.error.message || 'Unknown error',
  lastUpdated: Date.now(),
  isFetching: false
})

const injectBlueprintInitialValues = (
  interactionModelData: FlatInteractionModelData
) => {
  const { blueprints = [] } = interactionModelData || {}

  blueprints.forEach((blueprint) => {
    if (blueprint.initial_values) {
      setFlatInteractionModelActivities(
        interactionModelData,
        blueprint.initial_values
      )
    }
  })
}

/**
 * The initial state for all groups has all nested groups initialized.
 * This function takes the structure from an existing group at any level
 * to create a new one.
 * This intends to avoid seeking for a parent group in all activity models
 * and parsing from there.
 */
const createEmptyGroupsFrom = (
  nestableGroups: NestableGroupValue
): NestableGroupValue[0] => {
  return {
    uuid: uuidv4(),
    values: Object.fromEntries(
      Object.entries(
        (nestableGroups[0]?.values || {}) as NestableGroupValue[0]['values']
      ).flatMap(([groupValueUuid, groupValueContent]) => {
        if (Array.isArray(groupValueContent)) {
          return [[groupValueUuid, [createEmptyGroupsFrom(groupValueContent)]]]
        }
        return []
      })
    )
  }
}

export const interactionModelInitialState: InteractionModel = {
  isFetching: false,
  flatData: undefined,
  error: '',
  geolocation: null,
  userUuid: '',
  userName: '',
  dateStartInteraction: '',
  language: '',
  workflowUuid: null,
  lastUpdated: 0,
  interactionFinished: false,
  dismissedInformationCards: [],
  isGeolocationRequestDismissed: false,
  isDraftApplied: false,
  isUpdatingDraft: false
}

export const {
  reducer: interactionModelReducer,
  actions: interactionModelActions
} = createAppSlice({
  name: 'InteractionModel',
  initialState: interactionModelInitialState,
  reducers: (create) => ({
    getInteractionModel: create.asyncThunk(
      async (idInteractionModel: string) => {
        const response = await api.getInteractionModels(
          idInteractionModel,
          false
        )

        const data = response?.data?.interaction_model

        if (!data) {
          return {}
        }

        const flatData = interactionModelDataToFlat(data)
        injectBlueprintInitialValues(flatData)

        // Send interaction_start event to Google Tag Manager
        googleTagManager.setDataLayer({
          event: googleTagManager.Events.INTERACTION_START,
          user_origin: googleTagManager.getOriginFromUrl(),
          interaction_uuid: flatData.uuid,
          interaction_name: flatData.des,
          current_step: 1
        })

        return {
          flatData
        }
      },
      {
        pending: requestPending,
        rejected: requestError,
        fulfilled: (state, action) => ({
          ...state,
          flatData: action.payload.flatData,
          isFetching: false,
          error: '',
          interactionFinished: true,
          lastUpdated: Date.now()
        })
      }
    ),

    setStateFromDraft: create.preparedReducer(
      (newState, draftVersion) => ({
        payload: { newState, draftVersion }
      }),
      (_, action) => {
        return {
          ...action.payload.newState,
          draftVersion: action.payload.draftVersion,
          isDraftApplied: true
        }
      }
    ),

    updateDraftRequest: create.reducer((state) => ({
      ...state,
      isUpdatingDraft: true
    })),

    updateDraftSuccess: create.preparedReducer(
      (newVersion) => ({ payload: { newVersion } }),
      (state, action) => {
        return {
          ...state,
          draftVersion: action.payload.newVersion,
          isUpdatingDraft: false
        }
      }
    ),

    updateDraftFailure: create.reducer((state) => ({
      ...state,
      isUpdatingDraft: false
    })),

    removeActivityGroup: create.preparedReducer(
      (
        activityModelUuid: string,
        parentPath: string[],
        removedGroupIndex: number
      ) => ({
        payload: { activityModelUuid, parentPath, removedGroupIndex }
      }),
      (state, action) => {
        if (!state.flatData) {
          console.error(
            'Called removeActivityGroup without an interaction model'
          )

          return
        }

        const groups = getProperty(state.flatData.activity_values, [
          ...action.payload.parentPath,
          action.payload.activityModelUuid
        ])

        if (Array.isArray(groups)) {
          groups.splice(action.payload.removedGroupIndex, 1)
        }
      }
    ),

    addActivityGroup: create.preparedReducer(
      (activityModelUuid: string, parentPath: string[]) => ({
        payload: { activityModelUuid, parentPath }
      }),
      (state, action) => {
        if (!state.flatData) {
          console.error('Called addActivityGroup without an interaction model')

          return
        }

        const parent = getProperty<NestableGroupValue[0]['values']>(
          state.flatData.activity_values,
          action.payload.parentPath
        )

        if (!parent) {
          console.error('Called addActivityGroup with invalid parent')

          return
        }

        const groups = Array.isArray(parent[action.payload.activityModelUuid])
          ? (parent[action.payload.activityModelUuid] as NestableGroupValue)
          : []

        parent[action.payload.activityModelUuid] = [
          ...groups,
          createEmptyGroupsFrom(groups)
        ]
      }
    ),

    updateActivities: create.preparedReducer(
      (activities: Activity[]) => ({ payload: { activities } }),
      (state, action) => {
        if (!state.flatData) {
          console.error('Called updateActivities without an interaction model')

          return
        }

        setFlatInteractionModelActivities(
          state.flatData,
          action.payload.activities
        )
      }
    ),

    setGeolocation: create.preparedReducer(
      (coords: InteractionModel['geolocation']) => ({ payload: { coords } }),
      (state, action) => ({ ...state, geolocation: action.payload.coords })
    ),

    setInitialConfiguration: create.preparedReducer(
      (
        userUuid: InteractionModel['userUuid'],
        userName: InteractionModel['userName'],
        dateStartInteraction: InteractionModel['dateStartInteraction'],
        language: InteractionModel['language']
      ) => ({
        payload: { userUuid, userName, dateStartInteraction, language }
      }),
      (state, action) => ({
        ...state,
        language: action.payload.language,
        dateStartInteraction: action.payload.dateStartInteraction,
        userName: action.payload.userName,
        userUuid: action.payload.userUuid
      })
    ),

    setWorkflow: create.preparedReducer(
      (workflowUuid: InteractionModel['workflowUuid']) => ({
        payload: { workflowUuid }
      }),
      (state, action) => {
        if (state.workflowUuid !== action.payload.workflowUuid) {
          state.flatData = undefined
        }

        state.workflowUuid = action.payload.workflowUuid
      }
    ),

    setConfigToNewInteraction: create.preparedReducer(
      (dateStartInteraction: InteractionModel['dateStartInteraction']) => ({
        payload: { dateStartInteraction }
      }),
      (state, action) => ({
        ...state,
        dateStartInteraction: action.payload.dateStartInteraction,
        interactionFinished: false,
        isGeolocationRequestDismissed: false,
        dismissedInformationCards: []
      })
    ),

    dismissInformationCard: create.preparedReducer(
      (cardUuid: string) => ({ payload: { cardUuid } }),
      (state, action) => ({
        ...state,
        dismissedInformationCards: [
          ...state.dismissedInformationCards,
          action.payload.cardUuid
        ]
      })
    ),

    dismissGeolocationRequest: create.reducer((state) => ({
      ...state,
      isGeolocationRequestDismissed: true
    })),

    activateActivityModels: create.preparedReducer(
      (activityModelsUuids: string[]) => ({
        payload: { activityModelsUuids }
      }),
      (state, action) => {
        if (!state.flatData) {
          console.error(
            'Called activateActivityModels without an interaction model'
          )

          return
        }

        const { activityModelsUuids } = action.payload

        activityModelsUuids.forEach((uuid) => {
          const activityModel = state.flatData?.activity_models?.[uuid]

          if (activityModel) {
            activityModel.hidden = false
            activityModel.disabled = false
          }
        })
      }
    ),

    disableAndClearActivityModels: create.preparedReducer(
      (activityModelsUuids: string[]) => ({
        payload: { activityModelsUuids }
      }),
      (state, action) => {
        if (!state.flatData) {
          console.error(
            'Called disableActivityModels without an interaction model'
          )

          return
        }

        const { activityModelsUuids } = action.payload

        activityModelsUuids.forEach((uuid) => {
          const activityModel = state.flatData?.activity_models?.[uuid]
          const activityValues = state.flatData?.activity_values

          if (activityModel && activityValues != undefined) {
            activityModel.disabled = true
            activityValues[uuid] = ''
          }
        })
      }
    ),

    initializeDraft: create.reducer((state) => ({
      ...state,
      isDraftApplied: true,
      draftVersion: '1'
    }))
  })
})
